import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { companyService } from '../../services/companyService';
import { authService } from '../../services/authService';
import CompanyNav from '../../components/CompanyNav';
import CompanyBottomNavigation from '../../components/CompanyBottomNavigation';
import Loader from '../../components/Loader';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';

const EditProfile = () => {
  const user = authService.getCurrentUser();
  const { t, locale } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    bio: '',
    address: '',
    site_link: '',
    email: '',
    phone: '',
    employees_count: '',
    pdf: null,
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [currentPdf, setCurrentPdf] = useState(null);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await companyService.getProfile();
      const company = data?.data ?? data;
      setForm({
        name: company?.name ?? '',
        bio: company?.bio ?? '',
        address: company?.address ?? '',
        site_link: company?.site_link ?? '',
        email: company?.email ?? '',
        phone: company?.phone ?? '',
        employees_count: company?.employees_count ?? '',
        pdf: company?.pdf ?? null,
      });
      setCurrentPdf(company?.pdf ?? null);
    } catch (error) {
      const message = error?.response?.data?.message || t('company.profile.loadFailed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error(t('company.editProfile.pdfInvalid') || 'Please upload a PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error(t('company.editProfile.pdfSizeError') || 'PDF file size must not exceed 10MB');
        return;
      }
      setPdfFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('bio', form.bio || '');
      formData.append('address', form.address || '');
      formData.append('site_link', form.site_link || '');
      formData.append('email', form.email || '');
      formData.append('phone', form.phone || '');
      formData.append('employees_count', form.employees_count === '' ? '' : Number(form.employees_count));

      if (pdfFile) {
        formData.append('pdf', pdfFile);
      }

      await companyService.updateProfile(formData);
      toast.success(t('company.editProfile.saveSuccess'));
      // Reload profile to get updated PDF URL
      await loadProfile();
      setPdfFile(null);
    } catch (error) {
      const message = error?.response?.data?.message || t('company.editProfile.saveFailed');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const navigate = useNavigate();
  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return <Loader fullScreen={true} size="large" />;
  }

  return (
    <div className="employee-home-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>

      {/* Header */}
      <div className="settings-header d-flex justify-content-between align-items-center px-3 py-3" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="notification-icon">
          <span className="bell-icon">🔔</span>
        </div>
        <div className="settings-title">{t('company.editProfile.title')}</div>
        <button className="settings-back-btn" onClick={handleBack}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points={locale === 'ar' ? "15 18 9 12 15 6" : "9 18 15 12 9 6"}></polyline>
          </svg>
        </button>
      </div>


      <div className="all-jobs-section px-3 mb-5">
        {/* Creative header banner */}
        <div className="edit-hero mb-3">
          <div className="edit-hero-inner">
            <div className="edit-hero-badge">⚙️</div>
            <div className="edit-hero-text">
              <div className="edit-hero-title">{t('company.editProfile.title')}</div>
              <div className="edit-hero-subtitle">{t('company.editProfile.subtitle') || 'Keep your information updated for easy communication'}</div>
            </div>
          </div>
        </div>

        {/* Modern edit card */}
        <div className="edit-card mb-4">
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            <div className="grid-2">
              <div className="form-group-modern">
                <label className="form-label">{t('company.editProfile.name')}</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="form-control-modern"
                  placeholder={t('company.editProfile.name')}
                  required
                />
              </div>
              <div className="form-group-modern">
                <label className="form-label">{t('company.editProfile.employeesCount')}</label>
                <input
                  type="number"
                  name="employees_count"
                  value={form.employees_count}
                  onChange={handleChange}
                  className="form-control-modern"
                  min="0"
                  placeholder="0"
                />
              </div>
              <div className="form-group-modern">
                <label className="form-label">{t('company.editProfile.email')}</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="form-control-modern"
                  placeholder="email@company.com"
                />
              </div>
              <div className="form-group-modern">
                <label className="form-label">{t('company.editProfile.phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="form-control-modern"
                  placeholder="05xxxxxxxx"
                />
              </div>
              <div className="form-group-modern">
                <label className="form-label">{t('company.editProfile.address')}</label>
                <input
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  className="form-control-modern"
                  placeholder={t('company.editProfile.address')}
                />
              </div>
              <div className="form-group-modern">
                <label className="form-label">{t('company.editProfile.website')}</label>
                <input
                  type="url"
                  name="site_link"
                  value={form.site_link}
                  onChange={handleChange}
                  className="form-control-modern"
                  placeholder="https://example.com"
                />
              </div>
              <div className="form-group-modern grid-span-2">
                <label className="form-label">{t('company.editProfile.bio')}</label>
                <textarea
                  name="bio"
                  value={form.bio}
                  onChange={handleChange}
                  className="form-control-modern"
                  placeholder={t('company.editProfile.bioPlaceholder') || 'Brief description about the company'}
                  rows={7}
                />
              </div>
              <div className="form-group-modern grid-span-2">
                <label className="form-label">{t('company.editProfile.pdf') || 'Company PDF'}</label>

                {/* Current PDF Display */}
                {currentPdf && (
                  <div className="pdf-preview-card" style={{
                    background: 'linear-gradient(135deg, #f0f7ff 0%, #e8f2ff 100%)',
                    border: '2px dashed #4a7bc4',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)'
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', color: '#111', marginBottom: '4px' }}>
                          {t('company.editProfile.currentPdf') || 'Current PDF'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {t('company.editProfile.clickToView') || 'Click to view'}
                        </div>
                      </div>
                    </div>
                    <a
                      href={currentPdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        background: 'linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '10px 16px',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 4px 12px rgba(74, 123, 196, 0.3)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(74, 123, 196, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(74, 123, 196, 0.3)';
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                      {t('company.editProfile.view') || 'View'}
                    </a>
                  </div>
                )}

                {/* Modern File Upload Area */}
                <div className="pdf-upload-area" style={{
                  position: 'relative',
                  border: `2px dashed ${pdfFile ? '#4a7bc4' : '#d1d5db'}`,
                  borderRadius: '12px',
                  padding: '24px',
                  background: pdfFile ? 'linear-gradient(135deg, #f0f7ff 0%, #e8f2ff 100%)' : '#fafafa',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = '#4a7bc4';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #f0f7ff 0%, #e8f2ff 100%)';
                }}
                onDragLeave={(e) => {
                  e.currentTarget.style.borderColor = pdfFile ? '#4a7bc4' : '#d1d5db';
                  e.currentTarget.style.background = pdfFile ? 'linear-gradient(135deg, #f0f7ff 0%, #e8f2ff 100%)' : '#fafafa';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file && file.type === 'application/pdf') {
                    handlePdfChange({ target: { files: [file] } });
                  }
                }}
                >
                  <input
                    id="pdf-upload-input"
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handlePdfChange}
                    style={{
                      position: 'absolute',
                      opacity: 0,
                      width: '100%',
                      height: '100%',
                      cursor: 'pointer',
                      top: 0,
                      left: 0
                    }}
                  />

                  {pdfFile ? (
                    <div>
                      <div style={{
                        width: '64px',
                        height: '64px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        borderRadius: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px',
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)'
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <div style={{ fontWeight: '600', color: '#111', marginBottom: '4px', fontSize: '16px' }}>
                        {pdfFile.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                        {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPdfFile(null);
                          document.getElementById('pdf-upload-input').value = '';
                        }}
                        style={{
                          background: 'none',
                          border: '1px solid #dc2626',
                          color: '#dc2626',
                          borderRadius: '8px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          marginTop: '8px'
                        }}
                      >
                        {t('company.editProfile.remove') || 'Remove'}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div style={{
                        width: '64px',
                        height: '64px',
                        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                        borderRadius: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                        boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)'
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                      </div>
                      <div style={{ fontWeight: '600', color: '#111', marginBottom: '8px', fontSize: '16px' }}>
                        {t('company.editProfile.uploadPdf') || 'Upload PDF Document'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                        {t('company.editProfile.dragDropPdf') || 'Drag & drop your PDF here, or click to browse'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                        {t('company.editProfile.pdfHint') || 'PDF files only (max 10MB)'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="actions">
              <button
                type="submit"
                className="btn-primary-modern"
                disabled={saving}
              >
                {saving ? t('company.editProfile.saving') : t('company.editProfile.save')}
              </button>
            </div>
          </form>
        </div>

        {/* Local styles */}
        <style>
          {`
            .edit-hero {
              background: linear-gradient(135deg, rgba(96,143,211,0.15) 0%, rgba(74,123,196,0.15) 100%);
              border-radius: 18px;
              padding: 18px;
            }
            .edit-hero-inner {
              display: flex;
              align-items: center;
              gap: 12px;
            }
            .edit-hero-badge {
              width: 44px;
              height: 44px;
              border-radius: 12px;
              display: grid;
              place-items: center;
              background: linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%);
              color: #fff;
              font-size: 22px;
              box-shadow: 0 10px 20px rgba(76, 116, 179, 0.25);
            }
            .edit-hero-title {
              font-weight: 800;
              font-size: 1.1rem;
            }
            .edit-hero-subtitle {
              color: #475569;
              font-size: 0.9rem;
            }
            .edit-card {
              background: #ffffff;
              border-radius: 16px;
              padding: 18px;
              box-shadow: 0 10px 30px rgba(0,0,0,0.05);
              border: 1px solid #eef2f7;
            }
            .grid-2 {
              display: grid;
              grid-template-columns: 1fr;
              gap: 14px;
            }
            @media (min-width: 768px) {
              .grid-2 {
                grid-template-columns: 1fr 1fr;
              }
              .grid-span-2 {
                grid-column: span 2;
              }
            }
            .form-group-modern {
              display: flex;
              flex-direction: column;
              gap: 6px;
            }
            .form-control-modern {
              width: 100%;
              border: 1px solid #e5e7eb;
              background: #fafafa;
              padding: 12px 14px;
              border-radius: 12px;
              outline: none;
              transition: box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
            }
            .form-control-modern:focus {
              border-color: #4a7bc4;
              background: #fff;
              box-shadow: 0 0 0 4px rgba(74, 123, 196, 0.15);
            }
            .actions {
              display: flex;
              justify-content: flex-start;
              margin-top: 12px;
            }
            .btn-primary-modern {
              background: linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%);
              color: #fff;
              border: none;
              border-radius: 12px;
              padding: 12px 20px;
              font-weight: 700;
              cursor: pointer;
              box-shadow: 0 10px 20px rgba(76, 116, 179, 0.25);
            }
            .btn-primary-modern:disabled {
              opacity: 0.75;
              cursor: not-allowed;
              box-shadow: none;
            }
          `}
        </style>
      </div>

      <CompanyBottomNavigation />
    </div>
  );
};

export default EditProfile;


